<template>
    <div class="relative grid w-full grid-cols-[auto_1fr_auto] gap-3 p-2">
        <!-- All player rows -->
        <PlayerScoreBar
            v-for="player in orderedPlayers"
            :key="player.id"
            :player="player"
            :color="getProfileColor(player.id)"
            :status-points="getEffectiveState(player).statusPoints"
            :max-status-points="maxStatusPoints"
            :lives="getEffectiveState(player).lives"
            :max-lives="maxLives"
            :mode="getBarMode(player)"
            :can-interact="canPlayerInteract(player)"
            :can-remove="canPlayerRemove(player)"
            :size="getBarSize(player)"
            :overlay="getBarOverlay(player)"
            :killed="isEliminated(player)"
            :is-active="player.id === currentPlayer?.id"
            :target-section="getPlayerTarget(player.id)"
            :is-killer="getPlayerIsKiller(player.id)"
            @click-plus="handleClickPlus(player)"
            @click-minus="handleClickMinus(player)"
            @click-kill="handleClickKill(player)"
        />

        <!-- Complete Turn / Undo buttons -->
        <div class="relative col-span-3 bg-zinc-700 h-12 clip-diagonal-corners">
            <div class="inset-0.5 clip-diagonal-corners absolute flex gap-2 bg-black">
                <img
                    src="@/assets/images/games/killer/undo.svg"
                    class="h-full"
                    :class="
                        totalClicks > 0
                            ? 'cursor-pointer hover:bg-gray-100'
                            : 'cursor-not-allowed opacity-50'
                    "
                    :title="$t('killer.reset_pending_clicks')"
                    @click="resetAllClicks"
                />
                <div
                    class="flex-1 relative m-1 clip-diagonal-corners"
                    :style="{ backgroundColor: currentPlayerColor }"
                    :class="isCompletingTurn ? 'cursor-not-allowed opacity-50' : ''"
                    data-testid="killer-confirm-turn"
                    @click="confirmDartSelections"
                >
                <div class="mt-1 w-full absolute bg-black/10 text-md font-title clip-diagonal-corners flex cursor-pointer items-center justify-center p-1 text-center font-bold text-white uppercase transition-all select-none">

                    <div v-if="isCompletingTurn" class="flex items-center gap-2">
                        <div
                        class="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                        ></div>
                        <span>{{ $t('killer.completing') }}</span>
                    </div>
                    <span v-else>{{ $t('killer.complete_turn') }}</span>
                </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import PlayerScoreBar from './PlayerScoreBar.vue'
import { useMatchStore } from '@/stores/match.js'
import { DART_TYPES } from '@/constants/dartTypes.js'
import { MAX_STATUS_POINTS } from './killerConstants.js'

const DARTS_PER_TURN = 3

const BAR_OVERLAYS = {
    killer: 'killer.killer_mode_activated',
    killed: 'killer.killed',
}

export default {
    name: 'KillerPlayerList',
    components: { PlayerScoreBar },
    props: {
        currentPlayer: {
            type: Object,
            required: true,
        },
        profiles: {
            type: Array,
            default: () => [],
        },
        allProfileStates: {
            type: Array,
            default: () => [],
        },
        getProfileColor: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            isCompletingTurn: false,
            killerTransition: null,
            inFlightOwnClicks: 0,
            frozenStates: null,
            /** Pending click counts per player id. Positive = hits on own target / kills on other. */
            pendingClicks: {},
        }
    },
    computed: {
        matchStore() {
            return useMatchStore()
        },
        currentPlayerState() {
            if (!this.currentPlayer) return null
            return this.allProfileStates.find((state) => state.team === this.currentPlayer.team)
        },
        isCurrentPlayerKiller() {
            return this.currentPlayerState?.is_killer || false
        },
        /** Effective status points = server status points + pending own clicks, capped */
        effectiveHits() {
            return Math.min(
                MAX_STATUS_POINTS,
                this.currentPlayerHits +
                    (this.pendingClicks[this.currentPlayer?.id] || 0) +
                    this.inFlightOwnClicks,
            )
        },
        /** Whether player has become killer via pending clicks (status points at max) */
        becameKillerThisTurn() {
            return !this.isCurrentPlayerKiller && this.effectiveHits >= MAX_STATUS_POINTS
        },
        /** Treat player as killer if server says so OR pending clicks made them one */
        effectiveIsKiller() {
            return this.isCurrentPlayerKiller || this.becameKillerThisTurn
        },
        currentPlayerHits() {
            return this.currentPlayerState?.hits_on_own_target || 0
        },
        currentPlayerTarget() {
            return this.currentPlayerState?.number || null
        },
        orderedPlayers() {
            const current = this.profiles.find((p) => p.id === this.currentPlayer?.id)
            const others = this.profiles.filter((p) => p.id !== this.currentPlayer?.id)
            return current ? [current, ...others] : others
        },
        /** Total absolute clicks across all players this turn */
        totalClicks() {
            return Object.values(this.pendingClicks).reduce((sum, v) => sum + Math.abs(v), 0)
        },
        serverTurn() {
            const sets = this.matchStore.getMatchData?.sets
            const legs = sets?.[sets.length - 1]?.legs
            const turns = legs?.[legs.length - 1]?.turns
            return turns?.[turns.length - 1] || null
        },
        dartsThrownThisTurn() {
            if (this.serverTurn?.profile_id !== this.currentPlayer?.id) return 0
            return this.serverTurn.darts?.length || 0
        },
        remainingDartSlots() {
            return DARTS_PER_TURN - this.dartsThrownThisTurn - (this.killerTransition ? 1 : 0)
        },
        currentPlayerColor() {
            return this.getProfileColor(this.currentPlayer?.id)
        },
        maxStatusPoints() {
            return MAX_STATUS_POINTS
        },
        maxLives() {
            return this.matchStore.getMatchData?.settings?.starting_lives || 3
        },
        eliminatedIds() {
            return this.profiles.filter((player) => this.isEliminated(player)).map((player) => player.id)
        },
    },
    watch: {
        effectiveHits: {
            immediate: true,
            handler(statusPoints) {
                this.$emit('status-points-change', statusPoints)
            },
        },
        eliminatedIds(now, before) {
            this.profiles
                .filter((player) => now.includes(player.id) && !before.includes(player.id))
                .filter((player) => this.getPlayerLives(player.id) > 0)
                .forEach((player) => this.$emit('player-eliminated', player))
        },
    },
    emits: [
        'confirm-selection',
        'reset-selection',
        'became-killer',
        'player-eliminated',
        'status-points-change',
    ],
    methods: {
        getBarSize(player) {
            if (player.id === this.currentPlayer?.id) return 'large'
            if (this.isEliminated(player)) return 'small'
            return 'normal'
        },
        getBarOverlayType(player) {
            if (this.isEliminated(player)) return 'killed'
            if (player.id === this.currentPlayer?.id && this.effectiveIsKiller) return 'killer'
            return null
        },
        getBarOverlay(player) {
            const type = this.getBarOverlayType(player)
            return type ? this.$t(BAR_OVERLAYS[type]) : null
        },
        isEliminated(player) {
            if (!this.getTeamState(player.id)) return false
            return this.getEffectiveState(player).lives === 0
        },
        getTeamState(playerId) {
            const player = this.profiles.find((p) => p.id === playerId)
            if (!player) return null
            return this.allProfileStates.find((state) => state.team === player.team) || null
        },
        getEffectiveState(player) {
            if (this.frozenStates?.[player.id]) return this.frozenStates[player.id]
            if (player.id === this.currentPlayer?.id) {
                return { statusPoints: this.effectiveHits, lives: this.getPlayerLives(player.id) }
            }
            const statusPoints = this.getTeamState(player.id)?.hits_on_own_target || 0
            const lives = this.getPlayerLives(player.id)
            const damage = this.pendingClicks[player.id] || 0
            const absorbed = Math.min(statusPoints, damage)
            return {
                statusPoints: statusPoints - absorbed,
                lives: Math.max(0, lives - (damage - absorbed)),
            }
        },
        getEffectiveLives(playerId) {
            const player = this.profiles.find((p) => p.id === playerId)
            if (!player) return 0
            return this.getEffectiveState(player).lives
        },
        getPlayerLives(playerId) {
            return this.getTeamState(playerId)?.lives || 0
        },
        getPlayerTarget(playerId) {
            return this.getTeamState(playerId)?.number || null
        },
        getPlayerIsKiller(playerId) {
            if (this.frozenStates?.[playerId]) return this.frozenStates[playerId].isKiller
            if (playerId === this.currentPlayer?.id) {
                return this.effectiveIsKiller
            }
            const player = this.profiles.find((p) => p.id === playerId)
            if (!player) return false
            return (
                (this.getTeamState(playerId)?.is_killer || false) &&
                this.getEffectiveState(player).statusPoints >= MAX_STATUS_POINTS
            )
        },
        canAddClick(player) {
            const clicks = {
                ...this.pendingClicks,
                [player.id]: (this.pendingClicks[player.id] || 0) + 1,
            }
            const dartsNeeded = Object.values(clicks).reduce(
                (sum, count) => sum + Math.ceil(Math.abs(count) / 3),
                0,
            )
            return dartsNeeded <= this.remainingDartSlots
        },
        getBarMode(player) {
            if (player.id === this.currentPlayer?.id) {
                // Current player always gets plus/minus
                return 'score'
            }
            // Other players get kill button
            return 'kill'
        },
        canPlayerInteract(player) {
            if (this.isCompletingTurn) return false
            if (player.id === this.currentPlayer?.id) {
                // When effectively killer, can't add more own-target clicks
                if (this.effectiveIsKiller) return false
                return !!this.currentPlayerTarget && this.canAddClick(player)
            }
            // Other players can be attacked only when effectively killer and not already effectively dead
            return (
                this.effectiveIsKiller &&
                this.getEffectiveLives(player.id) > 0 &&
                this.canAddClick(player)
            )
        },
        canPlayerRemove(player) {
            if (this.isCompletingTurn) return false
            if (player.id === this.currentPlayer?.id) {
                // Can undo own plus clicks (but not if already submitted for killer transition)
                if (this.isCurrentPlayerKiller) return false
                return (this.pendingClicks[player.id] || 0) > 0
            }
            // Can undo kill clicks on other players
            return (this.pendingClicks[player.id] || 0) > 0
        },
        async handleClickPlus(player) {
            if (this.isCompletingTurn) return
            if (!this.canAddClick(player)) return
            if (player.id !== this.currentPlayer?.id) return
            if (this.effectiveIsKiller) return

            const newClicks = (this.pendingClicks[player.id] || 0) + 1
            this.pendingClicks = {
                ...this.pendingClicks,
                [player.id]: newClicks,
            }

            // If status points + pending clicks reach the max, auto-submit a dart to become killer
            const newEffectiveHits = this.currentPlayerHits + newClicks
            if (newEffectiveHits >= MAX_STATUS_POINTS) {
                await this.submitKillerTransitionDart(newClicks)
            }
        },
        handleClickMinus(player) {
            const current = this.pendingClicks[player.id] || 0
            if (current <= 0) return

            this.pendingClicks = {
                ...this.pendingClicks,
                [player.id]: current - 1,
            }
        },
        handleClickKill(player) {
            if (this.isCompletingTurn) return
            if (!this.canAddClick(player)) return
            if (player.id === this.currentPlayer?.id) return
            if (!this.effectiveIsKiller) return
            if (this.getEffectiveLives(player.id) <= 0) return

            this.pendingClicks = {
                ...this.pendingClicks,
                [player.id]: (this.pendingClicks[player.id] || 0) + 1,
            }
        },
        /**
         * Submit a single dart for the own-target clicks to trigger killer status on server.
         * Converts the accumulated own-target clicks into optimal S/D/T and submits as dart 1.
         */
        async submitKillerTransitionDart(clicks) {
            if (this.killerTransition) return
            const section = this.currentPlayerTarget
            if (!section) return

            const profileId = this.currentPlayer.id
            const dart = this.clicksToDart(clicks, section)
            const dartIndex = this.dartsThrownThisTurn + 1
            this.inFlightOwnClicks = clicks
            this.pendingClicks = { ...this.pendingClicks, [profileId]: 0 }
            this.$emit('became-killer')

            this.killerTransition = this.matchStore
                .submitDart({
                    profile_id: profileId,
                    dart_number: dart.dart_number,
                    dart_type: dart.dart_type,
                    dart_index: dartIndex,
                })
                .catch((error) => {
                    console.error('Failed to submit killer transition dart:', error)
                    this.pendingClicks = {
                        ...this.pendingClicks,
                        [profileId]: (this.pendingClicks[profileId] || 0) + clicks,
                    }
                })
                .finally(() => {
                    this.inFlightOwnClicks = 0
                    this.killerTransition = null
                })

            await this.killerTransition
        },
        snapshotEffectiveStates() {
            return Object.fromEntries(
                this.profiles.map((player) => [
                    player.id,
                    { ...this.getEffectiveState(player), isKiller: this.getPlayerIsKiller(player.id) },
                ]),
            )
        },
        /** Convert a click count into a single optimal dart */
        clicksToDart(clicks, section) {
            if (clicks >= 3) return { dart_type: DART_TYPES.TRIPLE, dart_number: section }
            if (clicks === 2) return { dart_type: DART_TYPES.DOUBLE, dart_number: section }
            return { dart_type: DART_TYPES.SINGLE, dart_number: section }
        },
        resetAllClicks() {
            if (this.isCompletingTurn) return
            if (this.totalClicks === 0) return
            // Note: if a killer transition dart was already submitted, we can't undo it.
            // Only reset pending clicks for remaining darts.
            this.pendingClicks = {}
        },
        /**
         * Build darts from pending clicks, same pattern as CamelraceGame.
         * Each player's click count is turned into optimal dart combos
         * (Triple > Double > Single) targeting that player's section number.
         */
        buildDartsFromClicks() {
            const darts = []

            for (const player of this.orderedPlayers) {
                const clicks = Math.abs(this.pendingClicks[player.id] || 0)
                if (clicks === 0) continue

                const section = this.getPlayerTarget(player.id)
                if (!section) continue

                let remaining = clicks
                while (remaining > 0) {
                    if (remaining >= 3) {
                        darts.push({ dart_type: DART_TYPES.TRIPLE, dart_number: section })
                        remaining -= 3
                    } else if (remaining === 2) {
                        darts.push({ dart_type: DART_TYPES.DOUBLE, dart_number: section })
                        remaining -= 2
                    } else {
                        darts.push({ dart_type: DART_TYPES.SINGLE, dart_number: section })
                        remaining -= 1
                    }
                }
            }

            // Pad to exactly the remaining dart slots with misses
            while (darts.length < this.remainingDartSlots) {
                darts.push({ dart_type: DART_TYPES.MISS, dart_number: 0 })
            }

            return darts.slice(0, this.remainingDartSlots)
        },
        async confirmDartSelections() {
            if (!this.currentPlayer || this.isCompletingTurn) return

            this.isCompletingTurn = true
            this.frozenStates = this.snapshotEffectiveStates()
            try {
                const profileId = this.currentPlayer.id
                await this.killerTransition
                const darts = this.buildDartsFromClicks()

                // Clear pending clicks BEFORE submitting so server responses
                // don't cause double-subtraction in getEffectiveState
                this.pendingClicks = {}

                for (const dart of darts) {
                    if (this.serverTurn?.profile_id !== profileId) break
                    await this.matchStore.submitDart({
                        profile_id: profileId,
                        dart_number: dart.dart_number,
                        dart_type: dart.dart_type,
                        dart_index: this.dartsThrownThisTurn + 1,
                    })
                }

                this.$emit('confirm-selection')
            } catch (error) {
                console.error('Failed to complete turn:', error)
            } finally {
                this.isCompletingTurn = false
                this.frozenStates = null
            }
        },
    },
}
</script>
